import {
    type TooltipTriggerComponentProps,
    type TooltipProps as AriaTooltipProps,
    TooltipTriggerStateContext,
} from 'react-aria-components'
import * as Styled from './tooltip.styled'
import {
    cloneElement,
    type ComponentPropsWithoutRef,
    createContext,
    type ElementType,
    type PropsWithChildren,
    type ReactElement,
    type RefObject,
    use,
    useRef,
} from 'react'
import {mergeProps, useTooltipTrigger, useObjectRef, Focusable} from 'react-aria'

interface TooltipProps extends TooltipTriggerComponentProps, Pick<AriaTooltipProps, 'offset'> {
    content: React.ReactNode | (() => React.ReactNode)
    placement?: 'top' | 'bottom' | 'left' | 'right'
    maxWidth?: string
}

const TooltipPropContext = createContext<Partial<TooltipProps>>({})

//TODO: Add SVG into overlay to show the arrow
export function Tooltip(props: TooltipProps) {
    // Assign / override some defaults
    const propsWithDefaults: TooltipProps = {
        ...props,
        delay: props.delay ?? 1000,
        closeDelay: props.closeDelay ?? 250,
        offset: props.offset ?? 8,
        placement: props.placement ?? 'top',
        maxWidth: props.maxWidth ?? '200px',
    }

    const {children, content, offset, placement, maxWidth, ...restOfProps} = propsWithDefaults

    // Explicitly add a ref to the trigger component that the user gave
    // so we can explicitly define a `triggerRef` prop for the `AriaTooltip`.
    // This is because We want to avoid having this problem
    // https://github.com/adobe/react-spectrum/issues/5665
    // It can happen for example if you try to put a Button in a tooltip.

    const triggerRef = useRef<HTMLElement>(null)
    let child = children as ReactElement
    child = cloneElement(child, mergeProps(child.props as object, {ref: triggerRef}))

    return (
        <TooltipPropContext value={propsWithDefaults}>
            <Styled.TooltipTrigger {...restOfProps}>
                {child}

                {content ? (
                    <Styled.Tooltip
                        offset={offset}
                        placement={placement}
                        triggerRef={triggerRef}
                        maxWidth={maxWidth}
                        shouldFlip={true}
                    >
                        {content}
                    </Styled.Tooltip>
                ) : null}
            </Styled.TooltipTrigger>
        </TooltipPropContext>
    )
}

// @deprecated This component is deprecated and will be removed in a
// future version.
// Please use {@link https://react-spectrum.adobe.com/react-aria/Tooltip.html#custom-trigger | Focusable} instead.
//
// Inspired by https://github.com/adobe/react-spectrum/issues/5733#issuecomment-1918691983
// Workaround to get tooltip to work with any element
// Cause the trigger must be a hoverable and focusable element
// that uses the useHover / useFocus hook.
// React-aria doesn't show tooltip for disabled elements by default.
// If you're using our Base button, it doesn't need to be wrapped
// in this component, unless  you want it to also display tooltip
// when its disabled.
export const TooltipTrigger = function ({
    ref,
    ...props
}: PropsWithChildren & {
    ref?: React.RefObject<HTMLElement>
}) {
    const triggerRef = useObjectRef(ref)
    const state = use(TooltipTriggerStateContext)
    const ctx = use(TooltipPropContext)
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!state || !ctx) {
        throw new Error('TooltipTrigger must be used within a Tooltip')
    }

    const {triggerProps} = useTooltipTrigger(ctx, state, triggerRef)

    const {children} = props
    if (!children) {
        throw new Error('TooltipTrigger must have a valid child')
    }

    return (
        <span {...mergeProps(triggerProps)} ref={triggerRef}>
            {children}
        </span>
    )
}

type TextTriggerProps<T extends ElementType = 'span'> = PropsWithChildren<{
    as?: T
    ref?: RefObject<T>
}> &
    Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'ref'>

/**
 * Wrapper element for child of <Focusable/> component from react-aria-components,
 * Use this if your tooltip trigger is not an interactive element.
 *
 * This is kind of a `hack` with react-aria because react-aria team is of the opinion of
 * only interactive element are supposed to have a tooltip.
 */
export function TextTrigger<T extends ElementType = 'span'>({
    as,
    children,
    ...restOfProps
}: TextTriggerProps<T>) {
    const Component = as || 'span'

    return (
        <Focusable>
            <Component role="button" {...restOfProps}>
                {children}
            </Component>
        </Focusable>
    )
}
